import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { services } from "../utils/constants";
import { stagger, blurUp, ruleGrow } from "../utils/motion";

const headVariants = blurUp;
const listVariants = stagger(0.1, 0.1);
const rowVariants = blurUp;

const Services = () => {
  return (
    <Wrapper>
      <span className="watermark" aria-hidden="true">
        Servis
      </span>
      <div className="section-center">
        <motion.header
          className="sec-head"
          variants={headVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.6 }}
        >
          <span className="kicker">
            <motion.span className="rule" aria-hidden="true" variants={ruleGrow} />
            Naše služby
          </span>
          <h2>
            Co pro vás <em>zařídíme</em>
          </h2>
        </motion.header>

        <motion.ul
          className="service-list"
          variants={listVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          {services.map((service, idx) => {
            const { id, icon, title, text } = service;
            return (
              <motion.li key={id} className="row" variants={rowVariants}>
                <span className="num">0{idx + 1}</span>
                <span className="icon" aria-hidden="true">
                  {icon}
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
                <span className="row-arrow" aria-hidden="true">
                  →
                </span>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  position: relative;
  padding: 6.5rem 0 7rem;
  background: var(--clr-dark-1);
  background-image: radial-gradient(rgba(250, 244, 234, 0.03) 1px, transparent 1px);
  background-size: 4px 4px;
  overflow: hidden;

  .watermark {
    position: absolute;
    right: -2rem;
    top: 1.5rem;
    font-family: var(--ff-script);
    font-size: clamp(8rem, 22vw, 17rem);
    line-height: 1;
    color: transparent;
    -webkit-text-stroke: 1px rgba(184, 115, 51, 0.18);
    pointer-events: none;
    user-select: none;
  }

  .sec-head {
    margin-bottom: 3.5rem;
    max-width: 640px;
  }
  .kicker {
    display: inline-flex;
    align-items: center;
    gap: 0.9rem;
    font-family: var(--ff-body);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    color: var(--clr-accent-light);
    margin-bottom: 1rem;
  }
  .kicker .rule {
    display: inline-block;
    width: 40px;
    height: 1px;
    background: var(--clr-accent);
    transform-origin: left;
  }
  h2 {
    color: var(--clr-cream);
    font-size: clamp(2rem, 4.5vw, 3.1rem);
    letter-spacing: -0.015em;
    margin: 0;
  }
  h2 em {
    font-style: italic;
    font-weight: 500;
    color: var(--clr-accent-light);
  }

  .service-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .row {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "num icon"
      "title title"
      "text text";
    align-items: center;
    gap: 0.75rem 1.25rem;
    padding: 2rem 0.5rem;
    border-top: 1px solid rgba(250, 244, 234, 0.14);
    transition: padding 450ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .row:last-child {
    border-bottom: 1px solid rgba(250, 244, 234, 0.14);
  }
  /* copper wash sweeping in from the left */
  .row::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      rgba(184, 115, 51, 0.16),
      rgba(184, 115, 51, 0.04) 55%,
      transparent 85%
    );
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
  }
  /* copper line drawing itself along the top edge */
  .row::after {
    content: "";
    position: absolute;
    top: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--clr-accent);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 650ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .row:hover::before,
  .row:hover::after {
    transform: scaleX(1);
  }
  .row:hover .num {
    color: var(--clr-accent-light);
    transform: translateX(4px) scale(1.12);
  }
  .row:hover .row-arrow {
    opacity: 1;
    transform: translateX(0);
  }

  .num {
    grid-area: num;
    font-family: var(--ff-display);
    font-style: italic;
    font-size: 1.6rem;
    font-weight: 500;
    color: rgba(250, 244, 234, 0.35);
    transition: color 400ms ease, transform 450ms cubic-bezier(0.22, 1, 0.36, 1);
    line-height: 1;
  }

  .icon {
    grid-area: icon;
    justify-self: end;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 1px dashed rgba(184, 115, 51, 0.5);
    color: var(--clr-accent-light);
    transition: var(--transition);
  }
  .icon svg {
    font-size: 1.35rem;
  }
  .row:hover .icon {
    border-style: solid;
    border-color: var(--clr-accent);
    background: var(--clr-accent);
    color: var(--clr-paper);
    transform: rotate(-6deg) scale(1.08);
  }

  h3 {
    grid-area: title;
    font-family: var(--ff-display);
    font-size: 1.5rem;
    color: var(--clr-cream);
    margin: 0;
    letter-spacing: 0;
  }

  p {
    grid-area: text;
    color: rgba(250, 244, 234, 0.72);
    font-size: 0.98rem;
    line-height: 1.7;
    margin: 0;
    max-width: 56ch;
  }

  .row-arrow {
    display: none;
  }

  @media (min-width: 900px) {
    .row {
      grid-template-columns: 64px 72px 1.1fr 1.6fr 48px;
      grid-template-areas: "num icon title text arrow";
      gap: 2rem;
      padding: 2.4rem 1rem;
    }
    .row:hover {
      padding-left: 2.1rem;
      padding-right: 0.4rem;
    }
    .icon {
      justify-self: start;
    }
    .num {
      font-size: 1.85rem;
    }
    h3 {
      font-size: 1.6rem;
    }
    .row-arrow {
      grid-area: arrow;
      justify-self: end;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 46px;
      height: 46px;
      border-radius: 50%;
      border: 1px solid rgba(216, 154, 90, 0.6);
      font-size: 1.1rem;
      color: var(--clr-accent-light);
      opacity: 0;
      transform: translateX(-12px);
      transition: opacity 400ms ease,
        transform 450ms cubic-bezier(0.22, 1, 0.36, 1);
    }
  }
`;

export default Services;
